"use client"

import { FormikProvider, useFormik } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAssignKnowledgeBaseFolderMutation } from "@/hooks/mutations/knowledge-base-folders"
import type {
  KnowledgeBaseAgentRef,
  KnowledgeBaseFolderView,
} from "@/server-actions/knowledge-base-folders"

const AGENT_TYPE_FULL_LABELS: Record<KnowledgeBaseAgentRef["type"], string> = {
  email: "Email agent",
  message: "Messaging agent",
  voice: "Voice agent",
}

type FolderAssignmentFormValues = {
  folderId: string
  agentType: KnowledgeBaseAgentRef["type"]
  agentId: string
  assign: boolean
}

type KnowledgeBaseFolderAssignmentMenuProps = {
  folder: KnowledgeBaseFolderView
  agents: KnowledgeBaseAgentRef[]
  onUpdated?: () => void | Promise<void>
}

export function KnowledgeBaseFolderAssignmentMenu({
  folder,
  agents,
  onUpdated,
}: KnowledgeBaseFolderAssignmentMenuProps) {
  const assignFolder = useAssignKnowledgeBaseFolderMutation({
    onSuccess: async () => {
      await onUpdated?.()
    },
  })

  const formik = useFormik<FolderAssignmentFormValues>({
    initialValues: {
      folderId: folder.id,
      agentType: "voice",
      agentId: "",
      assign: true,
    },
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        const agent = agents.find(item => item.id === values.agentId && item.type === values.agentType)
        await assignFolder.mutateAsync({
          ...values,
          agentName: agent?.name,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling already shows the user-facing toast and maps validation errors.
      }
    },
  })

  const submitAssignment = async (agent: KnowledgeBaseAgentRef, assign: boolean) => {
    await formik.setValues({
      folderId: folder.id,
      agentType: agent.type,
      agentId: agent.id,
      assign,
    })
    await formik.submitForm()
  }

  if (assignFolder.isPending) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  }

  return (
    <FormikProvider value={formik}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={agents.length === 0} className="h-12 w-full text-base sm:h-9 sm:w-auto sm:text-sm">
            Assign agent
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-h-[min(20rem,calc(100dvh-8rem))] w-[calc(100vw-2rem)] max-w-md sm:w-96"
        >
          {agents.length === 0 ? (
            <DropdownMenuItem disabled>No agents available</DropdownMenuItem>
          ) : (
            agents.map(agent => {
              const isAssigned = folder.agents.some(assigned => assigned.id === agent.id && assigned.type === agent.type)
              return (
                <DropdownMenuItem
                  key={`${agent.type}-${agent.id}`}
                  className="min-w-0 whitespace-normal break-words"
                  onClick={() => void submitAssignment(agent, !isAssigned)}
                >
                  <span>{isAssigned ? "Unassign" : "Assign"}</span> {AGENT_TYPE_FULL_LABELS[agent.type]}<span>:</span> {agent.name}
                </DropdownMenuItem>
              )
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </FormikProvider>
  )
}
